import axios from "axios"
import store from "@/store"
import router from "@/router"

const http = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || "http://localhost:8000/api/v1",
  timeout: parseInt(process.env.VUE_APP_API_TIMEOUT || "10000"),
  headers: { "Content-Type": "application/json" },
})

// Attach token to every request
http.interceptors.request.use((config) => {
  const token = store.state.auth?.accessToken
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// On 401: try silent refresh, retry once; on second 401 logout
let isRefreshing = false
let pendingQueue = []

function drainQueue(error, token = null) {
  pendingQueue.forEach((cb) => (error ? cb.reject(error) : cb.resolve(token)))
  pendingQueue = []
}

http.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config
    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error)
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({ resolve, reject })
      }).then((token) => {
        original.headers.Authorization = `Bearer ${token}`
        return http(original)
      })
    }

    original._retry = true
    isRefreshing = true

    try {
      const newToken = await store.dispatch("auth/refreshToken")
      drainQueue(null, newToken)
      original.headers.Authorization = `Bearer ${newToken}`
      return http(original)
    } catch (refreshError) {
      drainQueue(refreshError)
      store.dispatch("auth/logout")
      router.push("/login").catch(() => {})
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  }
)

export const varietiesApi = {
  list: (params) => http.get("/varieties/", { params }),
  get: (id) => http.get(`/varieties/${id}/`),
  create: (data) => http.post("/varieties/", data),
  update: (id, data) => http.patch(`/varieties/${id}/`, data),
  remove: (id) => http.delete(`/varieties/${id}/`),
}

export const gardenApi = {
  list: () => http.get("/gardens/"),
  get: (id) => http.get(`/gardens/${id}/`),
  create: (data) => http.post("/gardens/", data),
  update: (id, data) => http.patch(`/gardens/${id}/`, data),
  dashboard: (id) => http.get(`/gardens/${id}/dashboard/`),
}

export const gardenVarietyApi = {
  list: (params) => http.get("/garden-varieties/", { params }),
  create: (data) => http.post("/garden-varieties/", data),
  update: (id, data) => http.patch(`/garden-varieties/${id}/`, data),
  remove: (id) => http.delete(`/garden-varieties/${id}/`),
  toggleStatus: (id) => http.patch(`/garden-varieties/${id}/toggle_status/`),
}

export const profileApi = {
  get: () => http.get("/users/me/profile/"),
  update: (data) => http.patch("/users/me/profile/", data),
}

export const tasksApi = {
  list: (params) => http.get("/tasks/", { params }),
  create: (data) => http.post("/tasks/", data),
  update: (id, data) => http.patch(`/tasks/${id}/`, data),
  remove: (id) => http.delete(`/tasks/${id}/`),
  toggle: (id) => http.patch(`/tasks/${id}/toggle/`),
}
